import * as React from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import { Link } from 'libs/router';

import SystemMessageWrapperContext from '../wrapper/context/SystemMessageWrapperContext';
import SystemMessageNegotiationsReopenedDetails from './details/SystemMessageNegotiationsReopenedDetails';

import { __t, applyTranslations } from 'utils/language';
import { FUNC_RETURN_NULL } from '../utils/system-message';
import { getOfferRenegotiationMessageKey } from 'utils/offers/offers';

import './system-message-negotiations-reopened.scss';

applyTranslations(require('./system-message-negotiations-reopened.i18n.json'));

import type { MessengerAdditionalCompensation } from 'types';
import type { SystemMessageNegotiationsReopenedDetailsType } from './types';

const MSG_KEY = 'system_message_negotiations_reopened';

const SystemMessageNegotiationsReopened = () => {
  const context = React.useContext(SystemMessageWrapperContext);
  const {
    userType,
    campaign,
    message,
    isBrand,
    renderAvatar = FUNC_RETURN_NULL,
    renderDisclaimer = FUNC_RETURN_NULL,
    renderInfluencerActions = FUNC_RETURN_NULL
  } = context;

  const subcampaign = get(message, 'metadata.subcampaign', {});
  const { offer, updatedOffer } = subcampaign;

  const isNewOffer = offer?.state === 'pending' && isNil(updatedOffer);
  const isRenegotiation = !isNewOffer && !isNil(updatedOffer);
  const additionalCompensation: MessengerAdditionalCompensation = get(message, 'metadata.additionalCompensation', {});
  const disclaimer = __t(`${MSG_KEY}.messages.${userType}.disclaimer`);
  const disclaimerPopoverBody = __t(`${MSG_KEY}.messages.${userType}.disclaimer_popover`);
  const disclaimerPopoverTitle = __t(`${MSG_KEY}.labels.offer_expiration`);

  const iconName = !isNewOffer
    ? 'report-inside-circle-custom-fg'
    : 'letter-inside-circle-custom-fg';

  const renderPrimaryText = (): React.ReactNode => {
    const brandProfileName = get(campaign, 'brandProfile.name', __t(`${MSG_KEY}.labels.brand`));
    const textKey = isNil(updatedOffer) ? 'new_offer_from_quote' : 'important_offer_change';
    const text = __t(`${MSG_KEY}.messages.${textKey}`, {
      brandProfileName
    });

    return (
      <div
        className="system-message-negotiations-reopened__primary-text"
        title={text}
      >
        {text}
      </div>
    );
  };

  const renderSecondaryText = (): React.ReactNode => {
    if (isNil(updatedOffer)) {
      return null;
    }

    const textKey = getOfferRenegotiationMessageKey(offer, updatedOffer);
    const text = `${__t(`${MSG_KEY}.messages.${textKey}`)}: `;

    return (
      <div
        className="system-message-negotiations-reopened__secondary-text"
        title={text}
      >
        {text}
      </div>
    );
  };

  const renderLink = (): React.ReactNode => {
    const text = __t(`${MSG_KEY}.labels.view_brief`);

    return (
      <Link
        to={`/campaigns/${campaign?.id}/${isBrand ? 'subcampaigns' : 'details'}/${subcampaign.id}`}
        target="_blank"
        className="system-message-negotiations-reopened__link"
        title={text}
      >
        {text}
      </Link>
    );
  };

  const renderAdditionalCompensationDescription = (
    labelKey: string,
    description: string
  ): React.ReactNode => {
    const text = `${__t(`${MSG_KEY}.labels.${labelKey}`)}: ${description}`;

    return (
      <div
        className="system-message-negotiations-reopened__description text-overflow-ellipsis"
        title={text}
      >
        {text}
      </div>
    );
  };

  const renderAdditionalCompensationDescriptions = (): React.ReactNode => {
    const currentOffer = !isNil(updatedOffer) ? updatedOffer : offer;
    const voucherDescription = additionalCompensation.voucherDescription;
    const otherDescription = additionalCompensation.otherDescription;

    if (currentOffer?.type === 'other' || (isEmpty(voucherDescription) && isEmpty(otherDescription))) {
      return null;
    }

    return (
      <div className="system-message-negotiations-reopened__descriptions">
        {
          !isEmpty(voucherDescription) &&
            renderAdditionalCompensationDescription('voucher_description', voucherDescription)
        }
        {
          !isEmpty(otherDescription) &&
            renderAdditionalCompensationDescription('other_compensation', otherDescription)
        }
      </div>
    );
  };

  const renderDetails = (
    type: SystemMessageNegotiationsReopenedDetailsType
  ): React.ReactNode => (
    <SystemMessageNegotiationsReopenedDetails
      type={type}
      additionalCompensation={additionalCompensation}
    />
  );

  return (
    <React.Fragment>
      {renderAvatar(iconName)}
      <div className="system-message-negotiations-reopened">
        {renderPrimaryText()}
        {renderSecondaryText()}
        {renderDetails('new')}
        {renderAdditionalCompensationDescriptions()}
        {isRenegotiation && renderDetails('agreed')}
        {!isBrand && renderInfluencerActions()}
        {renderDisclaimer(disclaimer, disclaimerPopoverBody, disclaimerPopoverTitle)}
        {renderLink()}
      </div>
    </React.Fragment>
  );
};

export default SystemMessageNegotiationsReopened;
